import React, { useEffect } from "react";
import { PolicyCustomizeSection } from "uis/Section";
import UI from "@mile-meeting/mile-ui";
import GoogleWorkspaceMemberIntegrationModal from "uis/MemberManage/GoogleWorkspaceMemberIntegrationModal/GoogleWorkspaceMemberIntegrationModal";
import { openGoogleOrgUnitPopup } from "pages/setting/member-manage/invite-modal/google-oauth-modal";
import { getMemberEmailList, getOrganizationUnitList } from "apis/google";
import { useToast } from "hook/useToast";
import GoogleSyncFeature from "./GoogleSyncFeature";
import GoogleSyncInfo from "./GoogleSyncInfo/GoogleSyncInfo";
import {
  createMemberIntegration,
  getGwsDomainList,
  getGwsOrgUnitList,
  getMemberChange,
  syncMember,
  TIntegrationCategory,
  TMemberChange,
  unlinkMemberIntegration,
  updateMemberIntegration,
  updateMemberIntegrationChannel,
} from "apis/member-integration";
import GoogleWorkspaceMemberSyncPreviewModal from "uis/MemberManage/GoogleWorkspaceMemberSyncPreviewModal/GoogleWorkspaceMemberSyncPreviewModal";
import { TMember } from "types/user.types";
import GoogleWorkspaceUnlinkModal from "uis/MemberManage/GoogleWorkspaceUnlinkModal/GoogleWorkspaceUnlinkModal";
import { AlarmChannel } from "apis/alarm-settings";
import GoogleWorkspaceOrgUnitMemberIntegrationModal from "uis/MemberManage/GoogleWorkspaceOrgUnitMemberIntegrationModal/GoogleWorkspaceOrgUnitMemberIntegrationModal";
import { LoadingModal } from "uis/modal";

type TLoading = {
  loading: boolean;
  message?: string;
  isShowWaitingText?: boolean;
};

type GoogleSyncSectionProps = {
  isSynced: boolean;
  syncedDomainList?: string[];
  syncedOrgUnitList?: string[];
  onUpdateMemberIntegration?: () => void;
  syncedCreatedNum?: number;
  syncedDeletedNum?: number;
  lastSyncedDate?: string;
  integratedById?: TMember;
  integrationCategory?: TIntegrationCategory;
  alarmChannel?: AlarmChannel;
  syncedId?: string;
};
const GoogleSyncSection: React.FC<GoogleSyncSectionProps> = ({
  isSynced,
  syncedDomainList,
  syncedOrgUnitList,
  onUpdateMemberIntegration,
  syncedCreatedNum,
  syncedDeletedNum,
  lastSyncedDate,
  integratedById,
  integrationCategory,
  alarmChannel,
  syncedId,
}) => {
  const [openSyncModal, setOpenSyncModal] = React.useState(false);
  const [domainList, setGoogleSyncDomainList] = React.useState<string[]>([]);
  const [googleRefreshToken, setGoogleRefreshToken] = React.useState<string>("");
  const [googleAccessToken, setGoogleAccessToken] = React.useState<string>("");
  const [openUnlinkModal, setOpenUnlinkModal] = React.useState(false);
  const [orgUnitPathList, setOrgUnitPathList] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState<TLoading>({ loading: false });
  const [integrationCategoryState, setIntegrationCategoryState] = React.useState<TIntegrationCategory>(
    integrationCategory ?? "DOMAIN"
  );
  const [openGoogleWorkspaceMemberSyncPreviewModal, setOpenGoogleWorkspaceMemberSyncPreviewModal] =
    React.useState(false);
  const [memberChangedList, setMemberChangedList] = React.useState<TMemberChange>();
  const [openOrgUnitSyncModal, setOpenOrgUnitSyncModal] = React.useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    if (localStorage.getItem("mile-state") === "OPEN_MEMBER_SYNC_PREVIEW_MODAL") {
      onOpenGoogleWorkspaceMemberSyncPreviewModal(integrationCategory ?? "DOMAIN");
      localStorage.removeItem("mile-state");
    }
  }, []);

  //* INFO: GWS 멤버 연동하기 모달을 열기 위한 로직입니다.
  const onOpenDomainSyncModal = () => {
    setLoading({ loading: true, message: "구글에 로그인 중입니다.", isShowWaitingText: false });
    //* INFO: GWS 멤버 연동하기 버튼을 클릭했을 때 로직입니다.
    openGoogleOrgUnitPopup(async (success, accessToken, refreshToken) => {
      if (success) {
        setLoading({ loading: true, message: "GWS 멤버 정보를 불러오고 있습니다." });
        setGoogleAccessToken(accessToken);
        setGoogleRefreshToken(refreshToken);

        //* GWS 도메인 조회 및 연동 모달 오픈
        const res = await getMemberEmailList(accessToken);
        if (!res.success) {
          showToast("Google Workspace 멤버 목록을 불러올 수 있는 권한이 없습니다.", "warning");
          setLoading({ loading: false });
          return;
        }
        if (res.success && res.email?.length === 0) {
          showToast("불러올 수 있는 Google Workspace 멤버 목록이 없습니다.", "warning");
          setLoading({ loading: false });
          return;
        }
        const uniqueDomains = [...new Set(res.email?.map((email) => email.split("@")[1]))];
        setGoogleSyncDomainList(uniqueDomains);
        setOpenSyncModal(true);
        setLoading({ loading: false });
      }

      if (!success) {
        setLoading({ loading: false });
      }
    });
  };

  //* INFO: GWS 멤버 연동 정보 변경하기 모달을 여는 로직입니다.
  const onOpenSyncInfoChangeModal = async () => {
    //* 도메인으로 연동한 경우 도메인 모달로 열기
    if (integrationCategory === "DOMAIN") {
      //* 도메인 정보 조회하기
      const domains = await getGwsDomainList();
      if (domains.success && domains.data) {
        setGoogleSyncDomainList(domains.data);
        setOpenSyncModal(true);
      }
      if (!domains.success && domains.message) {
        showToast(domains.message, "warning");
        return;
      }
    }
    if (integrationCategory === "ORG_UNIT") {
      //* 조직 단위로 연동한 경우 조직 단위 모달로 열기
      const res = await getGwsOrgUnitList();
      if (res.success && res.data && res.data.length > 0) {
        setOrgUnitPathList(res.data);
      } else if (res.success && res.data && res.data.length === 0) {
        showToast("조직 단위 연동을 위한 조직 정보가 없습니다.", "warning");
        return;
      } else if (res.success === false && res.message) {
        showToast(res.message, "warning");
        return;
      }
      setOpenOrgUnitSyncModal(true);
    }
  };

  //* INFO: GWS 멤버 연동 및 동기화 미리보기 모달 표시하는 로직입니다.
  const onHandleSync = async (category: TIntegrationCategory, orgPaths?: string[]) => {
    setLoading({ loading: true, message: "GWS 멤버 정보를 연동 중입니다." });

    const res = await createMemberIntegration(
      category,
      googleRefreshToken,
      true,
      category === "DOMAIN" ? domainList : [],
      category === "ORG_UNIT" ? orgPaths ?? [] : []
    );
    if (res.success) {
      //* 화면 업데이트
      onUpdateMemberIntegration?.();
      setOpenSyncModal(false);
      setIntegrationCategoryState(category);
      //* 동기화 미리보기 모달 열기
      onOpenGoogleWorkspaceMemberSyncPreviewModal(category);
    } else {
      showToast("멤버 연동에 실패했습니다. 다시 시도해 주세요.", "error");
      setLoading({ loading: false });
    }
  };

  //* INFO: GWS 멤버 재연동 및 동기화 미리보기 모달을 여는 로직입니다.
  const onHandleReSync = async (category: TIntegrationCategory, orgPaths?: string[]) => {
    setLoading({ loading: true, message: "GWS 멤버 정보를 연동 중입니다." });
    const res = await updateMemberIntegration(
      syncedId ?? "",
      category,
      true,
      category === "DOMAIN" ? domainList : [],
      category === "ORG_UNIT" ? orgPaths ?? [] : []
    );
    if (res.success) {
      //* 화면 업데이트
      onUpdateMemberIntegration?.();
      setOpenSyncModal(false);
      setIntegrationCategoryState(category);
      //* 동기화 미리보기 모달 열기
      onOpenGoogleWorkspaceMemberSyncPreviewModal(category);
    } else {
      showToast("멤버 연동에 실패했습니다. 다시 시도해 주세요.", "error");
    }
  };

  //* INFO: GWS 멤버 동기화 미리보기 모달을 여는 로직입니다.
  const onOpenGoogleWorkspaceMemberSyncPreviewModal = async (integrationCategory: TIntegrationCategory) => {
    const res = await getMemberChange();

    setLoading({ loading: false });

    if (res.success && res.data) {
      setMemberChangedList(res.data);
      setIntegrationCategoryState(integrationCategory);
      setOpenOrgUnitSyncModal(false);
      setOpenGoogleWorkspaceMemberSyncPreviewModal(true);
    }
  };

  //* INFO: GWS 멤버 동기화 로직입니다.
  const onHandleSyncMember = async () => {
    const res = await syncMember();
    if (res.success) {
      showToast(
        `Google Workspace 동기화(${memberChangedList?.added ? memberChangedList.added.length : 0}인 추가, ${memberChangedList?.toBeRemoved ? memberChangedList.toBeRemoved.length : 0}인 삭제)가 완료되었습니다.`,
        "success"
      );
      //* 화면 업데이트
      onUpdateMemberIntegration?.();
      setOpenGoogleWorkspaceMemberSyncPreviewModal(false);
    } else {
      showToast("멤버 동기화에 실패했습니다. 다시 시도해 주세요.", "error");
    }
  };

  //* INFO: GWS 멤버 연동 해제하기 모달을 열기 위한 로직입니다.
  const onClickUnlink = async () => {
    setOpenUnlinkModal(true);
  };

  //* INFO: GWS 연동 해제 로직입니다.
  const onHandleGWSUnlink = async () => {
    const res = await unlinkMemberIntegration();

    if (res.success) {
      //* 화면 업데이트
      onUpdateMemberIntegration?.();
      setOpenUnlinkModal(false);

      showToast("Google Workspace 연동이 해제되었습니다.", "success");
    } else {
      showToast("Google Workspace 연동 해제에 실패했습니다. 재시도해 주세요.", "error");
    }
  };

  //* INFO: GWS 변동 알림 채널 변경 로직입니다.
  const onHandleAlarmChannelChange = async (alarmChannel: AlarmChannel) => {
    const res = await updateMemberIntegrationChannel(alarmChannel);
    if (res.success) {
      //* 화면 업데이트
      onUpdateMemberIntegration?.();
      showToast("GWS 멤버 정보 변동 알림 채널이 변경되었습니다.", "success");
    } else {
      showToast("알림 채널 변경에 실패했습니다. 다시 시도해 주세요.", "error");
    }
  };

  //* INFO: GWS 조직 정보 불러오기 로직입니다.
  const getGoogleWorkspaceOrgUnitList = async (): Promise<boolean> => {
    if (googleAccessToken) {
      //* GWS 연동 전 조직 정보 불러오기
      const res = await getOrganizationUnitList(googleAccessToken);
      if (res.data) {
        const orgUnitPathList = res.data.organizationUnits.map((item: any) => item.orgUnitPath.toString());
        setOrgUnitPathList(orgUnitPathList);
        return true;
      } else if (res.message) {
        showToast(res.message, "warning");
        return false;
      }
    }
    if (!googleAccessToken) {
      //* GWS 연동 후 조직 정보 불러오기 (googleAccessToken이 없을 경우)
      const res = await getGwsOrgUnitList();
      if (res.success && res.data) {
        const orgUnitPathList = res.data;
        setOrgUnitPathList(orgUnitPathList);
        return true;
      }
      showToast("Google Workspace 조직 목록을 불러올 수 있는 권한이 없습니다.", "warning");
      return false;
    }
    return false;
  };

  //* INFO: GWS 도메인 정보 불러오기 로직입니다.
  const getGoogleWorkspaceDomainList = async () => {
    if (!googleAccessToken) {
      //* GWS 연동 전 도메인 정보 불러오기
      const res = await getGwsDomainList();
      if (res.data) {
        setGoogleSyncDomainList(res.data);
      }
      return;
    }
  };

  return (
    <PolicyCustomizeSection icon={<UI.Icon name="google" size={20} />} title="GWS 멤버 정보 연동 및 동기화">
      {!isSynced && <GoogleSyncFeature onHandleSync={onOpenDomainSyncModal} />}
      {isSynced && (
        <GoogleSyncInfo
          domainList={syncedDomainList}
          orgUnitList={syncedOrgUnitList}
          syncedCreatedNum={syncedCreatedNum}
          syncedDeletedNum={syncedDeletedNum}
          lastSyncedDate={lastSyncedDate}
          integratedById={integratedById}
          integrationCategory={integrationCategory}
          onHandleUnlink={onClickUnlink}
          onHandleSync={() => onOpenGoogleWorkspaceMemberSyncPreviewModal(integrationCategory ?? "DOMAIN")}
          alarmChannel={alarmChannel}
          onHandleAlarmChannelChange={onHandleAlarmChannelChange}
          onHandleSyncInfoChange={onOpenSyncInfoChangeModal}
        />
      )}
      <GoogleWorkspaceMemberIntegrationModal
        isReSync={syncedId ? true : false}
        onHandleReSync={() => onHandleReSync("DOMAIN")}
        onHandleSync={() => onHandleSync("DOMAIN")}
        modalIsOpen={openSyncModal}
        closeModal={() => setOpenSyncModal(false)}
        domainList={domainList}
        onHandleOrgUnitSync={async () => {
          setLoading({ loading: true, message: "GWS 연동 단위를 변경 중입니다" });
          setOpenSyncModal(false);
          const res = await getGoogleWorkspaceOrgUnitList();
          setLoading({ loading: false });
          if (res) {
            setOpenOrgUnitSyncModal(true);
          }
        }}
      />
      <GoogleWorkspaceOrgUnitMemberIntegrationModal
        modalIsOpen={openOrgUnitSyncModal}
        closeModal={() => setOpenOrgUnitSyncModal(false)}
        syncedOrgUnitList={syncedOrgUnitList ?? []}
        onHandleDomainSync={async () => {
          setLoading({ loading: true, message: "GWS 연동 단위를 변경 중입니다" });
          setOpenOrgUnitSyncModal(false);
          await getGoogleWorkspaceDomainList();
          setLoading({ loading: false });
          setOpenSyncModal(true);
        }}
        onHandleSync={(paths: string[]) => onHandleSync("ORG_UNIT", paths)}
        onHandleReSync={(paths: string[]) => onHandleReSync("ORG_UNIT", paths)}
        orgUnitPathList={orgUnitPathList}
        isResync={syncedId ? true : false}
      />
      <GoogleWorkspaceMemberSyncPreviewModal
        syncCategory={integrationCategoryState}
        modalIsOpen={openGoogleWorkspaceMemberSyncPreviewModal}
        closeModal={() => setOpenGoogleWorkspaceMemberSyncPreviewModal(false)}
        addMemberList={memberChangedList?.added.map((item) => item.email) ?? []}
        deleteMemberList={memberChangedList?.toBeRemoved.map((item) => item.email) ?? []}
        onHandleSync={onHandleSyncMember}
      />
      <GoogleWorkspaceUnlinkModal
        modalIsOpen={openUnlinkModal}
        onHandleUnlink={onHandleGWSUnlink}
        closeModal={() => setOpenUnlinkModal(false)}
      />
      <LoadingModal
        modalIsOpen={loading.loading}
        title={loading.message ?? ""}
        isShowWaitingText={loading.isShowWaitingText ?? true}
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(GoogleSyncSection);
